import * as members from '../../privates/privateMembers';
import valueSanitzationAlgorithm from '../../privates/utils/valueSanitzationAlgorithm';
import {validate} from '../../privates/validations/validation';
import {runValidationsAfterChange} from '../../privates/validationsService/validationsService';

/**
 * @mixin ValueMixin
 * @summary Adds the value property to elements.
 * @description
 *  `ValueMixin` is a mixin that provides basic properties and event handlers for elements on the page that have values.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  let myValue = $w("#ValueMixin").value;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `ValueMixin`. 
 * 
 *  ```javascript
 *  let myValue = $w("#myElement").value;
 *  ```
 *  
 * @memberof $w
 */
class ValueMixin {
    /**
     * @function onChange
     * @syntax
     * function onChange(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when an input element's value
     *  is changed.
     * @description
     *  An element receives a `change` event when a user changes the value
     *  in an input element.
     *
     *  A `change` event is not triggered when you change an element's value using
     *  the element's [`value`](#value) property.
     * 
     *  Because `onChange()` is fired before validations are performed, do not check 
     *  any of the validation properties in the `onChange()` event handler. For example, 
     *  do not code an if statement that checks the `valid` property in the `onChange()` 
     *  because the property at this point contains values prior to the `onChange()`.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * 
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element's value changes.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [FormElement-onChange.es6=Get the value of the element that was changed]
     * @memberof $w.ValueMixin
     * @eventtype change
     * @eventhandler
     */
    onChange(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onChange',
            dataToValidate: {onChange: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CHANGE, controllerId, cb);
        return this;
    }

    /**
     * @member value
     * @label property
     * @summary Sets or gets an element's value.
     * @description
     *  The `value` property's type depends on the element implementing it. 
     * 
     * > **Notes:**
     * > + Changing an element's `value` in code does not trigger an `onChange` event.
     * >
     * >
     * > + If an element is connected to a dataset, setting the element's `value`
     *  in code does not set the value of the connected field in the dataset.
     *  That means if you use the dataset to perform a submit, the value changed
     *  in code is not reflected in the submitted item.
     * >
     * >
     * > + To submit the new value using a dataset, set the field's value using the
     *  [`setFieldValue()`](wix-dataset.html#setFieldValue) function before
     *  performing the submit.
     * @snippet [FormElement-value_get.es6=Get an element's value]
     * @snippet [FormElement-value_set.es6=Set an element's value]
     * @type {*}
     * @memberof $w.ValueMixin
     */
    get value() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).value;
    }

  /**
   * @member value
   * @memberof $w.ValueMixin
   * @param {*} val
   */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const value = valueSanitzationAlgorithm(compData, val);
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value},
            compName: this.id
        });

        if (!isValid) {
            return;
        }

        runValidationsAfterChange(RMI, compId, value);
        RMI.setData(compId, { value });
    }
}

export default ValueMixin;
